/**
 * Metric Card Component
 *
 * Displays a single metric with current/previous comparison and trend sparkline
 */

import { Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";
import type { AbsoluteMetric } from "../types";
import { MetricTooltip } from "./metric-tooltip";
import { Sparkline } from "./sparkline";

interface MetricCardProps {
  metric: AbsoluteMetric;
  tooltip?: string;
  locked?: boolean;
}

function formatValue(value: number | null | undefined, format?: string): React.ReactNode {
  if (value === null || value === undefined) {
    return "-";
  }

  switch (format) {
    case "currency":
      return <PriceDisplay amount={value} />;
    case "percentage":
      return `${value.toFixed(1)}%`;
    case "duration":
      return `${Math.round(value)}m`;
    default:
      return new Intl.NumberFormat("en-US").format(value);
  }
}

export function MetricCard({ metric, tooltip, locked = false }: MetricCardProps) {
  const getTrendColor = () => {
    if (
      metric.value.change === 0 ||
      metric.value.change === null ||
      metric.value.change === undefined
    ) {
      return "text-gray-500";
    }

    // If inverse metric (lower is better), flip the colors
    return metric.value.isPositive ? "text-green-600" : "text-red-600";
  };

  const getTrendDirection = (): "up" | "down" | "neutral" => {
    if (
      metric.value.change === 0 ||
      metric.value.change === null ||
      metric.value.change === undefined
    ) {
      return "neutral";
    }
    return metric.value.isPositive ? "up" : "down";
  };

  const hasSparkline = metric.value.trend && metric.value.trend.length > 0;

  return (
    <Card
      className={cn(
        "animate-in fade-in slide-in-from-bottom-4 duration-500",
        locked && "group relative overflow-hidden cursor-pointer"
      )}
    >
      <CardHeader className="pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground flex items-center">
          {metric.label}
          {tooltip && <MetricTooltip content={tooltip} />}
        </CardTitle>
      </CardHeader>
      <CardContent className={hasSparkline ? "" : "pb-3"}>
        <div
          className={cn(
            hasSparkline ? "space-y-2" : "space-y-1",
            locked && "blur-[3px] pointer-events-none select-none opacity-50"
          )}
        >
          {/* Current value */}
          <div
            className={
              hasSparkline
                ? "text-xl md:text-2xl font-bold break-words"
                : "text-lg md:text-xl font-bold break-words"
            }
          >
            {formatValue(metric.value.current, metric.format)}
          </div>

          {/* Comparison */}
          <div className="flex items-center gap-2 text-xs">
            <div className={cn("font-medium", getTrendColor())}>
              {(metric.value.changePercent ?? 0) > 0 ? "+" : ""}
              {metric.value.changePercent?.toFixed(1) ?? "0.0"}
              {metric.format === "percentage" ? " pp" : "%"}
            </div>
            <span className="text-muted-foreground">
              vs {formatValue(metric.value.previous, metric.format)}
            </span>
          </div>

          {/* Sparkline */}
          {hasSparkline && metric.value.trend && (
            <Sparkline
              data={metric.value.trend}
              trend={getTrendDirection()}
              format={metric.format}
            />
          )}
        </div>
      </CardContent>

      {/* Premium overlay - shows on hover when locked */}
      {locked && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Lock className="h-6 w-6 text-muted-foreground mb-1" />
          <p className="text-xs font-semibold text-foreground">Premium</p>
        </div>
      )}
    </Card>
  );
}
